import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  type FormInstance,
} from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import { FormDropzone } from "../files/components/FormDropzone";
import type {
  Blade,
  Client,
  Location,
  Materials,
  Project,
  Services,
  Turbine,
  UserLimitedList,
} from "../generated-axios-client";
import { MultiAssigneeSelectorFormItem } from "./create-forms/MultiAssigneeSelectorFormItem";
import { CoordinatorSelectorFormItem } from "./create-forms/CoordinatorSelectorFormItem";
import { ManagerSelectorFormItem } from "./create-forms/ManagerSelectorFormItem";
import { TaskSelectorFormItem } from "./create-forms/TaskSelectorFormItem";
import { ClientSelectorFormItem } from "./create-forms/ClientSelectorFormItem";
import { LocationSelectorFormItem } from "./create-forms/LocationSelectorFormItem";
import { MaterialsSelectorFormItem } from "./create-forms/MaterialsSelectorFormItem";
import { ServiceSelectorFormItem } from "./create-forms/ServiceSelectorFormItem";
import { TurbineSelectorFormItem } from "./create-forms/TurbineSelectorFormItem";
import { ProjectSelectorFormItem } from "../../common/components/ProjectSelect";

type TicketFieldsProps = {
  dispatch: Dispatch;
  intl: IntlShape;
  serviceItems: Services[];
  userItems: UserLimitedList[];
  coordinatorItems: UserLimitedList[];
  managerItems: UserLimitedList[];
  turbineItems?: Turbine[];
  clientItems: Client[];
  bladeItems?: Blade[];
  locationItems: Location[];
  materialItems: Materials[];
  projectItems: Project[];
  form: FormInstance;
  modalAPI?: HookAPI;
};

export const TicketFields = (props: TicketFieldsProps) => {
  const {
    intl,
    serviceItems,
    userItems,
    coordinatorItems,
    managerItems,
    turbineItems,
    clientItems,
    bladeItems,
    locationItems,
    materialItems,
    projectItems,
    form,
    modalAPI,
    dispatch,
  } = props;

  return (
    <Flex vertical gap="middle">
      <Card title={intl.formatMessage({ id: "ticket_section_details" })}>
        <Row gutter={24}>
          <Col span={24}>
            <ServiceSelectorFormItem intl={intl} serviceItems={serviceItems} />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              name="description"
              label={intl.formatMessage({ id: "ticket_description" })}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "validation_message_ticket_description",
                  }),
                },
              ]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={6}>
            <ClientSelectorFormItem intl={intl} clientItems={clientItems} form={form} />
          </Col>
          <Col xs={24} md={6}>
            <TurbineSelectorFormItem
              intl={intl}
              turbineItems={turbineItems}
              form={form}
            />
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              name="client_case_nr"
              label={intl.formatMessage({ id: "ticket_client_case_number" })}
              rules={[
                {
                  required: true,
                  // pattern: /^\d{7}?/,
                  message: intl.formatMessage({
                    id: "validation_message_ticket_client_case_number",
                  }),
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <ProjectSelectorFormItem
              intl={intl}
              projectItems={projectItems}
              form={form}
              placeholder={intl.formatMessage({ id: "project_field_project_placeholder" })}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form.Item label={intl.formatMessage({ id: "ticket_start" })} name="start">
              <DatePicker
                showNow={false}
                style={{ width: "100%" }}
                placeholder={intl.formatMessage({ id: "ticket_start" })}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label={intl.formatMessage({ id: "ticket_deadline" })}
              name="deadline"
            >
              <DatePicker
                showNow={false}
                style={{ width: "100%" }}
                placeholder={intl.formatMessage({ id: "ticket_deadline" })}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_section_assignment" })}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <CoordinatorSelectorFormItem intl={intl} userItems={coordinatorItems} />
          </Col>
          <Col xs={24} md={12}>
            <LocationSelectorFormItem
              form={form}
              intl={intl}
              locationItems={locationItems}
              turbineItems={turbineItems}
            />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <ManagerSelectorFormItem intl={intl} userItems={managerItems} />
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24}>
            <MultiAssigneeSelectorFormItem intl={intl} userItems={userItems} />
          </Col>
        </Row>
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_tasks" })}>
        <TaskSelectorFormItem
          intl={intl}
          bladeItems={bladeItems}
          serviceItems={serviceItems}
          form={form}
          showUpload
          dispatch={dispatch}
          notifficationApi={modalAPI}
        />
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_section_supplies" })}>
        <MaterialsSelectorFormItem intl={intl} materialItems={materialItems} />
      </Card>

      <Card title={intl.formatMessage({ id: "ticket_section_files" })}>
        <Form.Item name="new_files">
          <FormDropzone dispatch={dispatch} intl={intl} notifficationApi={modalAPI} />
        </Form.Item>
      </Card>
    </Flex>
  );
};
